import { SelectChangeEvent } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { redirect, useFetcher, useLoaderData, useNavigate } from "react-router-dom";
import { useGlobal } from "../../components/GlobalProviderLayout";
import { SET_CURRENT_BUTTON } from "../../components/GlobalProviderLayout/GlobalProviderLayoutModel";
import { doFetch } from "../../utils/doFetch";
import { SurplusBoard } from "./SurplusBoard";
import { ButtonItem } from "./SurplusBoardData";
import { UnionBoard } from "./UnionBoard";
import { IPanel } from "./UnionBoardData";
import { Boards } from "./UnionBoardModels";

const fetchShifts = async (request: any, board: string) => {
  const shiftsResponse = await doFetch(`board/union-board/shifts/${board}`, { signal: request.signal }, true, false);
  const shifts: { shift: string, shiftDate: string, }[] = shiftsResponse.shifts;
  return shifts.map(s => ({ shift: s.shift.replace(":", ""), shiftDate: s.shiftDate.replaceAll(".", "") }));
}

const fetchBoardData = async (request: any, board: string, shift: string, date: string) => {
  return await doFetch(`board/union-board/${board}/${shift}/${date}`, { signal: request.signal }, true, false);
}

const fetchDefaultBoardData = async (request: any) => {
  return await doFetch("board/union-board", { signal: request.signal }, true, false);
}

const isSurplus = (board: string) => {
  return Boards[board as keyof typeof Boards] === Boards.sur;
}

export const buildDataObject = ({ board, shift, date, shifts, panels, scoreboard }: {
  board: string,
  shift: string,
  date: string,
  shifts: { shift: string, shiftDate: string, }[],
  panels: IPanel[],
  scoreboard: { callback: number },
}) => {
  return {
    selectedBoard: board.toLowerCase(),
    selectedShift: `${shift},${date}`,
    shifts: shifts.map(s => {
      return {
        display: dayjs(`${s.shift} ${s.shiftDate}`, "HHmm DDMMYYYY").format("ddd. MMM DD, YYYY HH:mm"),
        value: `${s.shift},${s.shiftDate}`
      }
    }),
    panels: panels,
    scoreboard,
  }
}

export const UnionBoardRouteLoader = async ({ request, params }: { request: any, params: any }) => {
  if (isSurplus(params.board)) {
    const data = await doFetch("board/union-board/sur", { signal: request.signal }, true, false);
    data.selectedBoard = params.board;
    return data;
  }
  // handles shift change
  if (params.board && params.shift && params.date) {
    const { board, shift, date } = params;
    const shifts = await fetchShifts(request, board);
    const { panels, scoreboard } = await fetchBoardData(request, board, shift, date);
    return buildDataObject({ board, shift, date, shifts, panels, scoreboard });

    // handles board change
  } else if (params.board) {
    const { board } = params;
    const shifts = await fetchShifts(request, board);
    const [{ shift, shiftDate: date }] = shifts;
    const { panels, scoreboard } = await fetchBoardData(request, board, shift, date);
    return buildDataObject({ board, shift, date, shifts, panels, scoreboard });
  } else {
    // handles default board
    const { criteria: { board } } = await fetchDefaultBoardData(request);
    return redirect(`/union-board/${board.toLowerCase()}`);
  }
}

export const UnionBoardRoute = () => {
  const navigate = useNavigate();
  let loaderData = useLoaderData();
  const { dispatch } = useGlobal();
  const fetcher = useFetcher();
  const data = fetcher.data ?? loaderData;
  const [regId, setRegId] = useState(0);

  const handleBoardSelect = (event: SelectChangeEvent<string>) => {
    const selectedBoard: string = event.target.value;
    navigate(`/union-board/${selectedBoard}`);
  };

  const handleShiftDateSelect = (event: SelectChangeEvent<string>, selectedBoard: string) => {
    const [shift, date] = event.target.value.split(",");
    fetcher.load(`/union-board/${selectedBoard}/${shift}/${date}`);
  };

  const handleOpenPlateModal = (id: number) => setRegId(id);

  const handleButtonClick = (button: ButtonItem) => {
    dispatch({ type: SET_CURRENT_BUTTON, payload: button });
    if (button) {
      navigate(`/union-board/${button.boardCode.toLocaleLowerCase()}`);
    }
  }

  if (isSurplus(data.selectedBoard)) {
    return (
      <SurplusBoard
        handleButtonClick={handleButtonClick}
        data={{ ...data, buttons: data.buttons ?? [] }}
        handleBoardSelect={handleBoardSelect}
      />
    );
  } else {
    return (
      <UnionBoard
        handleOpenPlateModal={handleOpenPlateModal}
        regIdForPlateModal={regId}
        handleClosePlateModal={() => setRegId(0)}
        data={data}
        handleBoardSelect={handleBoardSelect}
        handleShiftDateSelect={handleShiftDateSelect}
      />
    );
  }
}